import React, { useMemo } from "react";
import { ResponsiveContainer } from "recharts";

const getColor = (value) => {
  if (value >= 25) return "#800026";
  if (value >= 22.5) return "#BD0026";
  if (value >= 20) return "#E31A1C";
  if (value >= 17.5) return "#FC4E2A";
  if (value >= 15) return "#FD8D3C";
  if (value >= 12.5) return "#FEB24C";
  if (value >= 10) return "#FED976";
  if (value >= 7.5) return "#FFEDA0";
  return "#FFFFCC";
};

const legendTicks = [25, 22.5, 20, 17.5, 15, 12.5, 10, 7.5, 5];

const monthShort = (dateStr) => {
  const d = new Date(dateStr + "-01");
  return d?.toLocaleString("default", { month: "short" });
};

const HeatmapChart = ({ rawData }) => {
  const heatmap = rawData;

  const { months, values, loanTypes } = useMemo(() => {
    if (!heatmap) return { months: [], values: [], loanTypes: [] };

    const apiMonths = heatmap?.months;
    const apiValues = heatmap?.values;

    // take latest 6 months
    const lastIndex = apiMonths.length;
    const startIndex = Math?.max(0, lastIndex - 12);

    const latestMonths = apiMonths?.slice(startIndex, lastIndex);
    const latestValues = apiValues?.slice(startIndex, lastIndex);

    return {
      months: latestMonths?.map(monthShort),
      values: latestValues,
      loanTypes: heatmap?.loanTypes?.map((t) => t.replace(" Loan", "")),
    };
  }, [heatmap]);

  const cellWidth = 80;
  const cellHeight = 50;
  const chartWidth = loanTypes?.length * cellWidth + 100;
  const chartHeight = months?.length * cellHeight + 120;

  if (!months?.length) return null;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <svg width={chartWidth} height={chartHeight}>
        <svg>
          {/* Cells */}
          {months?.map((m, row) =>
            loanTypes?.map((l, col) => {
              const value = values[row]?.[col] ?? 0;

              return (
                <g key={`${m}-${l}`}>
                  <rect
                    x={col * cellWidth + 60}
                    y={row * cellHeight + 40}
                    width={cellWidth}
                    height={cellHeight}
                    fill={getColor(value)}
                    stroke="#fff"
                  />

                  <text
                    x={col * cellWidth + 60 + cellWidth / 2}
                    y={row * cellHeight + 40 + cellHeight / 2 + 5}
                    textAnchor="middle"
                    fontSize="14"
                  >
                    {value}
                  </text>
                </g>
              );
            }),
          )}

          {/* X Axis */}
          {loanTypes?.map((l, col) => (
            <text
              key={l}
              x={col * cellWidth + 60 + cellWidth / 2}
              y={months?.length * cellHeight + 70}
              textAnchor="middle"
              fontSize="12"
            >
              {l}
            </text>
          ))}

          <text
            x={(loanTypes?.length * cellWidth) / 2 + 60}
            y={months?.length * cellHeight + 95}
            textAnchor="middle"
            fontSize="12"
          >
            Loan Type
          </text>

          {/* Y Axis */}
          {months?.map((m, row) => (
            <text
              key={m}
              x={50}
              y={row * cellHeight + 40 + cellHeight / 2 + 5}
              textAnchor="end"
              fontSize="12"
            >
              {m}
            </text>
          ))}

          {/* Y label */}
          <text
            x={20}
            y={chartHeight / 2}
            textAnchor="middle"
            fontSize="12"
            transform={`rotate(-90, 10, ${chartHeight / 2})`}
          >
            Months
          </text>

          {/* Title */}
          <text
            x={(loanTypes?.length * cellWidth) / 2 + 60}
            y={30}
            textAnchor="middle"
            fontSize="14"
          >
            Average Loan Tenure (Months) Heatmap by Loan Type
          </text>
        </svg>
      </svg>

      {/* Legend */}
      <div
        style={{
          height: months?.length * cellHeight,
          display: "flex",
          alignItems: "center",
          position: "relative",
          paddingRight: "8px",
          marginBottom: "40px",
        }}
      >
        <div
          style={{
            height: months?.length * cellHeight,
            width: "20px",
            background:
              "linear-gradient(to top, #FFFFCC, #FFEDA0, #FED976, #FEB24C, #FD8D3C, #FC4E2A, #E31A1C, #BD0026, #800026)",
            border: "1px solid #ccc",
          }}
        />

        <div
          style={{
            height: months.length * cellHeight,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            fontSize: "12px",
            marginLeft: "4px",
          }}
        >
          {legendTicks?.map((tick, i) => (
            <span key={i}>{tick}</span>
          ))}
        </div>

        <div
          style={{
            position: "absolute",
            right: 0,
            top: "65%",
            left: "95%",
            transform: "translateY(-50%) rotate(-90deg)",
            fontSize: "12px",
            whiteSpace: "nowrap",
          }}
        >
          Loan Default Rate
        </div>
      </div>
    </div>
  );
};

export default HeatmapChart;
